import { AfterViewInit, Directive, inject, OnDestroy } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { DualCommunicatorService } from "../../../services/internal-services/dual-communicator.service";
import { BaseViewerComponent } from "./base-viewer.component";
import { Viewerlayer } from "../../../model/viewerlayer";
import { GeoService } from "../../../services/internal-services/geo.service";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { IUrlParams } from "../../../interfaces/i-url-params";
import { LayerUtilsService } from "../../../services/internal-services/layer-utils.service";
import { GeoJSONDatabaseService } from "../../../services/internal-services/geojson-database.service";
import { UserConfigCommunicatorService } from "../../../services/communicators/user-config-communicator.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Directive()
export abstract class BaseDualViewerComponent extends BaseViewerComponent implements OnDestroy, AfterViewInit {
  dualCommunicatorService = inject(DualCommunicatorService);

  geoJSONDatabaseService = inject(GeoJSONDatabaseService);

  userConfigurationService = inject(UserConfigCommunicatorService);

  dualView = false;

  private userConfigTimeOut: any;

  // region LifeCycle

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.dualCommunicatorService.dualEnabled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled: boolean) => {
        this.dualView = enabled;
        super.updateSizeMap();
      });

    this.viewerService.userLayers$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((layers: Viewerlayer[]) => {
      if (this.dualCommunicatorService.getUserLayers().length !== layers.length) {
        this.dualCommunicatorService.updateUserLayers(layers);
      }
    });

    this.userConfigurationService.userConfigResponse$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.userConfigTimeOut) {
        clearTimeout(this.userConfigTimeOut);
      }
      this.userConfigTimeOut = setTimeout(() => {
        const layers = this.getLayersToSaveConfiguration();
        this.viewerService.saveUserConfiguration(layers);
      }, 60000);
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.dualCommunicatorService.removeMap(this.mapId);
    if (this.userConfigTimeOut) {
      clearTimeout(this.userConfigTimeOut);
    }
  }

  // endregion
  // region Initialize Map

  handleFirstLoadEvent() {
    const urlParameters = this.urlStateService.getParamsFromURL();
    this.urlStateService.mergeUrlParams(urlParameters);
    if (
      urlParameters.address &&
      (!this.dualCommunicatorService.isDualEnabled() || this.dualCommunicatorService.startInfoProperties.length === 0)
    ) {
      this.handleSearchAddressInUrl(urlParameters.address, this.isMasterMap());
    } else if (urlParameters.latitude && urlParameters.longitude) {
      this.handleUrlParameters(urlParameters);
    }
  }

  /**
   * Configure map status, location and zoom
   * @param config
   * @param urlLocation
   */
  calculateInitLocation(config: any, urlLocation: IUrlParams): Observable<IUrlParams> {
    const location = { ...urlLocation };
    if (!this.isMasterMap() && this.dualCommunicatorService.startMapStatus) {
      // get location data from master map

      const geographicLocation = GeoService.toWgs84(
        this.dualCommunicatorService.startMapStatus.center.geometry.coordinates,
      );
      const lat = geographicLocation[1];
      const long = geographicLocation[0];
      location.latitude = lat;
      location.longitude = long;
      location.zoom = this.dualCommunicatorService.startMapStatus.zoom;
      return of(location);
    }
    // if is master map we try to get the location from url (address or lat/lng)
    if (urlLocation.address) {
      // get location data from address

      return GeoService.geocoding(urlLocation.address).pipe(
        map((result: any[]) => {
          const lat = result[0].geometry.location.lat();
          const long = result[0].geometry.location.lng();
          location.latitude = lat;
          location.longitude = long;
          if (!location.zoom && config.lastZoom) {
            location.zoom = config.lastZoom;
          }
          return location;
        }),
        first(),
        catchError((error) => {
          console.error("Error on calculateInitLocation geocoding " + error);
          return of(this.processUrlLocation(location, urlLocation, config));
        }),
      );
    }

    this.processUrlLocation(location, urlLocation, config);

    return of(location);
  }

  /**
   * Modify layers before initialize map
   * @param config
   */
  preprocessLayers(config: any): Viewerlayer[] {
    if (!config) {
      return [];
    }

    const layers = config.layers;

    if (!layers || layers.length === 0) {
      return layers || [];
    }

    // sync layer config from state url
    this.updateLayerConfig(config, layers);

    // check collection order
    //todo maybe we can remove this
    this.checkCollectionInfo(layers);

    // split collection layers in single xyz and discrete layers and add crop filter
    let processedLayers = layers;

    processedLayers
      .filter((layer) => layer.config)
      .map((layer) => {
        layer.enableCropFilter = !!(
          layer.config.filters && layer.config.filters.includes(ViewerConstants.LAYER_FILTERS.CROP)
        );
        return layer;
      });

    // if is not first map we sync with the master map
    if (!this.isMasterMap()) {
      processedLayers = this.layerService.syncronizeLayers(
        this.dualCommunicatorService.startBaseLayers.concat(this.dualCommunicatorService.startOverlayLayers),
        processedLayers,
      );
    }
    return processedLayers;
  }

  private checkCollectionInfo(layers: Viewerlayer[]) {
    const collections = layers.filter((layer) => layer.layerType === ViewerConstants.LAYER_TYPE.COLLECTIONLAYER);

    // Check if collection xyz layers has infrared
    collections.map(
      (collection) => (collection.enableInfrared = JSON.stringify(collection).indexOf("infraredExtraParam") !== -1),
    );
    collections.map(
      (collection) =>
        (collection.enabledMeasurements = JSON.stringify(collection).indexOf("enabledMeasurements") !== -1),
    );
  }

  /**
   * Update layers from state
   */
  updateLayerConfig(config: any, layers: Viewerlayer[]) {
    const stateParam = this.urlStateService.getStateParam(this.mapService.getMapId());

    this.setInitialAOI(stateParam, config, layers);

    // this.viewerService.handleState(stateParam);
    //Set
    this.setInitialSources(stateParam, config, layers);

    if (stateParam?.layers?.length) {
      LayerUtilsService.syncLayersConfiguration(layers, stateParam?.layers);
    }
  }

  /**
   * Configure map options and features
   * @param config
   */
  configureMapOptions(config: any) {
    this.viewerService.createMeasurementTools([
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_POINT_3D,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_ELEVATION,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_AREA,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_LINE,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_HEIGHT,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_POLYGON,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_POINT_SURVEY,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_POINT,
      ViewerConstants.EXTERNAL_TOOLS.RELATIVE_MEASURE_POINT,
      ViewerConstants.EXTERNAL_TOOLS.MEASURE_CIRCLE,
    ]);

    let measureSystem = null;

    if (config.measureSystem !== null) {
      this.viewerService.updateMeasurementSystem(config.measureSystem, true);
      measureSystem = ViewerConstants.MEASUREMENT_SYSTEMS.SYSTEMS.find(
        (system) => system.ID === config.measureSystem,
      );
      const isMetric = measureSystem.ID === 1;
      this.viewerService.updateMeasurementLengthUnit(measureSystem.UNITS.LENGTH_MIDDLE, true);
      this.viewerService.updateMeasurementAreaUnit(
        isMetric ? ViewerConstants.AREA_METRIC_UNITS[0] : ViewerConstants.AREA_IMPERIAL_UNITS[0],
        true
      );
      this.viewerService.updateMeasurementSlopeUnit(
        isMetric ? ViewerConstants.SLOPE_UNITS[0] : ViewerConstants.SLOPE_UNITS[1],
        true
      );
      this.dualCommunicatorService.changeUnitSelected(measureSystem.NAME.toLowerCase());
    }

    if (config.userMeasurements) {
      const userMeasurements = config.userMeasurements;
      if (userMeasurements.measureLengthUnit) {
        this.viewerService.updateMeasurementLengthUnit(userMeasurements.measureLengthUnit, true);
      }
      if (userMeasurements.measureAreaUnit) {
        this.viewerService.updateMeasurementAreaUnit(userMeasurements.measureAreaUnit, true);
      }
      if (userMeasurements.measurePitchUnit) {
        this.viewerService.updateMeasurementSlopeUnit(userMeasurements.measurePitchUnit, true);
      }
      if (userMeasurements.propertyInfoUnit !== undefined && userMeasurements.propertyInfoUnit !== null) {
        this.viewerService.updatePropertyInfoUnit(userMeasurements.propertyInfoUnit, true);
      }
    } else if (measureSystem) {
      this.viewerService.updatePropertyInfoUnit(measureSystem.ID, true);
    }
    // Determine initial global measurement system based on config
    this.initializeGlobalMeasurementSystem(config, measureSystem);

    this.viewerService.allowMeasurements();
    this.viewerService.allowProperties();
    this.viewerService.allowPropertiesReport();
    this.viewerService.allowExportGeoTiff();
  }

  /**
   * Launch save configuration service and launch initialization events
   * Update location url with last location
   */
  finishInitialization(config: any): void {
    if (!this.isMasterMap()) {
      this.dualCommunicatorService.updateEnableSync(true);
    } else {
      this.viewerConfigurationService.setReadNotifications(config.viewedNotifications);
    }
    //TODO: maybe we dont need the next 3 lines
    this.mapService.populateMapStatus(); // we dont need now
    // this.viewerService.updateCenterImageDate(); // we dont have images neither projects at this point
    /// /////////////////////////////////////////////////////////////////////////////////////////
    // We have to wait a small period of time before fire initializeMap event
    // this is needed due to a synchronization bug between OL viewer and GoogleMaps viewer
    /// /////////////////////////////////////////////////////////////////////////////////////////
    setTimeout(() => {
      this.viewerService.showLoadingComponent();
      this.viewerConfigurationService.initializedMap(true);
    }, 500);
  }

  // endregion

  enableContextMenu() {
    this.viewerService.enableContextMenu();
  }

  // region GetInfo // Gallery // Context menu
  openImageGalleryReload(): void {
    const mapCenter = this.viewerService.getLocation();
    forkJoin([
      this.viewerService.getAllAvailableImages(mapCenter),
      GeoService.reverseGeocode(mapCenter.reverse()),
    ]).subscribe((result: any) => {
      const images = result[0];
      const formattedAddress = result[1][0].formatted_address;
      if (images.length) {
        this.openImageGalleryDialog(mapCenter, images, formattedAddress);
      }
    });
  }

  configureDualView() {
    this.dualCommunicatorService.addMap(this.mapId, this.map);
  }

  // endregion

  private initializeGlobalMeasurementSystem(config: any, measureSystem: any) {
    const IDS = ViewerConstants.MEASUREMENT_SYSTEMS.IDS;

    if (config.userMeasurements) {
      const units = config.userMeasurements;
      const detectedSystems = new Set<number>();

      const checkUnit = (unit: string) => {
        if (!unit) return;
        if (ViewerConstants.METRIC_UNITS.includes(unit) ||
          ViewerConstants.LENGTH_METRIC_UNITS.includes(unit) ||
          ViewerConstants.AREA_METRIC_UNITS.includes(unit)) {
          detectedSystems.add(IDS.METRIC);
        } else if (ViewerConstants.IMPERIAL_UNITS.includes(unit) ||
          ViewerConstants.LENGTH_IMPERIAL_UNITS.includes(unit) ||
          ViewerConstants.AREA_IMPERIAL_UNITS.includes(unit)) {
          detectedSystems.add(IDS.IMPERIAL);
        }
      };

      checkUnit(units.measureLengthUnit);
      checkUnit(units.measureAreaUnit);
      checkUnit(units.measureUnit);

      if (units.propertyInfoUnit !== undefined && units.propertyInfoUnit !== null) {
        detectedSystems.add(units.propertyInfoUnit === IDS.IMPERIAL ? IDS.IMPERIAL : IDS.METRIC);
      }

      if (units.measureSystem !== undefined && units.measureSystem !== null) {
        detectedSystems.add(units.measureSystem === IDS.IMPERIAL ? IDS.IMPERIAL : IDS.METRIC);
      }

      if (detectedSystems.size > 1) {
        this.viewerConfigurationService.setGlobalMeasurementSystem(IDS.MIXED);
      } else if (detectedSystems.size === 1) {
        this.viewerConfigurationService.setGlobalMeasurementSystem(Array.from(detectedSystems)[0]);
      } else if (measureSystem) {
        this.viewerConfigurationService.setGlobalMeasurementSystem(measureSystem.ID);
      }
    } else if (measureSystem) {
      this.viewerConfigurationService.setGlobalMeasurementSystem(measureSystem.ID);
    } else if (config.measureSystem !== null && config.measureSystem !== undefined) {
      this.viewerConfigurationService.setGlobalMeasurementSystem(
        config.measureSystem === IDS.IMPERIAL ? IDS.IMPERIAL : IDS.METRIC
      );
    }
  }
}
